import React from 'react';

import { Box, Button, Stack, Typography } from '@mui/material';
import { Trash2Icon } from 'lucide-react';

import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { useTeamTransactionsSharing } from '@modules/reports/cma-reports/step-transaction/hooks/useTeamTransactionsSharing';
import { useLocale } from '@shared/hooks/locale';

type TransactionInfoDialogProps = {
  open: boolean;
  onClose: () => void;
  teamId?: string;
};

const TransactionInfoDialog: React.FC<TransactionInfoDialogProps> = ({
  open,
  onClose,
  teamId,
}) => {
  const { t } = useLocale();
  const { optOut, loading: updatingTeam } = useTeamTransactionsSharing(teamId);

  const handleOptOut = async () => {
    if (!teamId) {
      console.error('No team ID available for opt-out');
      return;
    }

    try {
      await optOut();
      onClose();
    } catch (error) {
      console.error('Failed to opt out:', error);
    }
  };

  const footerActions = (
    <Stack
      spacing={1}
      sx={{
        p: 2,
        width: '100%',
        backgroundColor: 'background.paper',
      }}
    >
      <Button
        variant="contained"
        onClick={onClose}
        disabled={updatingTeam}
        fullWidth
      >
        {t('No, keep my access to transactions')}
      </Button>
      <Button
        variant="outlined"
        color="error"
        startIcon={<Trash2Icon />}
        onClick={handleOptOut}
        loading={updatingTeam}
        loadingPosition="start"
        disabled={!teamId}
        fullWidth
      >
        {t('Yes, remove my transactions and access')}
      </Button>
    </Stack>
  );

  return (
    <SlideDialog
      open={open}
      onClose={onClose}
      title={t('Transactions data')}
      onSave={handleOptOut}
      loading={updatingTeam}
      saveDisabled={!teamId}
      saveText={t('Yes, remove my transactions and access')}
      cancelText={t('No, keep my access to transactions')}
      maxWidth="sm"
      footerActions={footerActions}
    >
      <Box sx={{ p: 2 }}>
        <Stack spacing={2}>
          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('Where does the data come from?')}
            </Typography>
            <Typography variant="body1">
              {t('Transaction data is collected from two sources:')}
            </Typography>
            <Typography variant="body1" sx={{ pl: 2 }}>
              {t('• External public records')}
            </Typography>
            <Typography variant="body1" sx={{ pl: 2 }}>
              {t(
                '• Transactions reported by RealAdvisor clients and published on their agent profiles',
              )}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('Are my transactions included?')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 0.5 }}>
              {t(
                'Yes, but only if they are published on your RealAdvisor profile.',
              )}
            </Typography>
            <Typography variant="body1">
              {t('These transactions contribute to the anonymised database.')}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('Is my data confidential?')}
            </Typography>
            <Typography variant="body1">
              {t(
                'Absolutely. Confidential information such as exact addresses, seller names, or agent details is never disclosed.',
              )}
            </Typography>
          </Box>

          <Box>
            <Typography
              variant="body1"
              sx={{
                fontWeight: 'bold',
              }}
            >
              {t('Can I remove my transactions?')}
            </Typography>
            <Typography variant="body1" sx={{ mb: 1 }}>
              {t(
                'Yes. You can remove your transactions at any time by clicking below. Please note: if you opt out, you will no longer have access to the transactions analysis tool in your CMA reports.',
              )}
            </Typography>
          </Box>
        </Stack>
      </Box>
    </SlideDialog>
  );
};

export default TransactionInfoDialog;
